import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { auth } from '../auth/appAuth';
import Menu from './overview/icons/Menu';
import ThemeToggle from './overview/toggle/ThemeToggle';
import { useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom';
import './overview/styles/OverviewHeader.css';
import './overview/styles/OverviewCards.css';
import { format, addDays, addWeeks, addMonths, isBefore, isEqual, startOfDay, endOfDay, subDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { usePermissions } from '../company-management/hooks/usePermissions';
import { useAppointmentAutoArchiver, useMeetingAutoArchiver } from '../components/archives/AutoArchiver.js';
import { useBackgroundAppointmentCompletion } from '../hooks/useBackgroundAppointmentCompletion';
import { useAppointmentUpdates } from '../hooks/useAppointmentUpdates';
import { generateShortSlug } from '../utils/helpers';
import { listAppointments, upsertAppointment } from '../api/appointments';
import { listMeetings } from '../api/meetings/meetings';
import { listClients } from '../api/companyRecords';

import PendingConfirmationsCard from './overview/PendingConfirmationsCard';
import { debounce } from 'lodash';
import TodayAppointmentsCard from './overview/TodayAppointmentsCard';
import RecentUpdatesCard from './overview/RecentUpdatesCard';
import NavigationMenu from './overview/NavigationMenu';
import QuickActionsColumn from './overview/QuickActionsColumn';
import MaintenanceNoticeBanner from './maintenance/MaintenanceNoticeBanner';
import AppEmailVerificationBanner from './email-verification/AppEmailVerificationBanner';
import { useAppEmailVerificationRestricted } from './email-verification/EmailVerificationRestrictionContext';
import { isAllowedPathWhileEmailUnverified } from './email-verification/appEmailVerificationRestriction';
import EmailVerificationOverviewGuard from './email-verification/EmailVerificationOverviewGuard';
import './email-verification/appEmailVerificationLock.css';
import AppointmentCard from './overview/AppointmentCard';
import UpcomingAppointmentsCard from './overview/UpcomingAppointmentsCard';
import AppointmentsPage from './appointments/AppointmentsPage';
import TutorialChecklist from './tutorial/TutorialChecklist';
import OverviewTabs from './overview/OverviewTabs';
import OverviewSideNav from './overview/side-nav/OverviewSideNav';
import './overview/side-nav/OverviewSideNav.css';
import SignOutConfirmDialog from './overview/sign-out-confirm/SignOutConfirmDialog';
import OverviewMobileBottomNav from './overview/mobile-bottom-nav/OverviewMobileBottomNav';
import {
  getOverviewMobileNavMode,
  isOverviewMobileNavPath,
} from './overview/mobile-bottom-nav/overviewMobileNavPaths';
import {
  OverviewMobilePendingPanel,
  OverviewMobileTodayPanel,
  OverviewMobileUpdatesPanel,
  useAppointmentMobileNavCounts,
} from './overview/mobile-panels/AppointmentMobilePanels';
import {
  MeetingsMobilePendingPanel,
  MeetingsMobileTodayPanel,
} from './overview/mobile-panels/MeetingsMobilePanels';
import './overview/mobile-bottom-nav/OverviewMobileBottomNav.css';
import './overview/mobile-panels/OverviewMobilePanels.css';
import { OverviewUserProvider } from '../context/OverviewUserContext';
import { archiveCompanyAppointment } from '../shared/booking/appointments/archiveCompanyAppointment';
import { getCompanyAppointmentUrlSegment } from '../shared/booking/appointments/companyAppointmentRouting';
import { sortAppointmentsByDateTimeCompletedLast } from '../utils/appointmentUtils';

const OVERVIEW_APPOINTMENT_PAST_DAYS = 1;
const OVERVIEW_APPOINTMENT_FUTURE_DAYS = 120;

// Add this helper function at the top level of the file, before any components
const convertToDate = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    // If it's already a Date
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    // If it's a Firestore Timestamp
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    
    // If it's a timestamp object with seconds
    if (dateValue?.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    
    // If it's a string or number
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    return null;
  }
};

const COMPANY_APPOINTMENT_STORAGE = 'company';

const normalizeOverviewAppointment = ({
  id,
  data,
  storageLocation = COMPANY_APPOINTMENT_STORAGE,
}) => ({
  id,
  ...data,
  dateTime: convertToDate(data.dateTime),
  endTime: convertToDate(data.endTime),
  updatedAt: convertToDate(data.updatedAt),
  createdAt: convertToDate(data.createdAt),
  storageLocation,
});

export const UpcomingAppointments = ({ 
  appointments, 
  setIsAddModalOpen, 
  handleQuickConfirm,
  handleQuickCancel,
  handleQuickReschedule,
  handleQuickNoShow,
  setConfirmationDialog,
  getServiceName,
  updates,
  handleAppointmentUpdate,
  companyId,
  onUpdateRead,
  onClearAllRead,
  appointmentsLoading = false,
  updatesLoading = false,
}) => {
  const [localAppointments, setLocalAppointments] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigate = useNavigate();
  const [isProcessingUpdate, setIsProcessingUpdate] = useState(false);
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [viewAllType, setViewAllType] = useState('all');
  const [isViewAllDialogOpen, setIsViewAllDialogOpen] = useState(false);

  const debouncedUpdateAppointments = useMemo(
    () => debounce((appointments) => {
      setLocalAppointments(appointments);
      setIsProcessingUpdate(false);
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateAppointments.cancel();
    };
  }, [debouncedUpdateAppointments]);

  useEffect(() => {
    setLocalAppointments(appointments || []);
    return () => {};
  }, [appointments]);

  const handleCalendarClick = () => {
    navigate('/overview/calendar');
  };

  const handleAppointmentClick = (appointment) => {
    if (!appointment?.id) return;
    const appointmentSegment = getCompanyAppointmentUrlSegment(appointment);
    navigate(`/app/overview/appointment/${appointmentSegment}`, {
      state: { appointment }
    });
  };

  // 1. Memoize the appointment filtering and sorting
  const filteredAppointments = useMemo(() => {
    if (!localAppointments?.length) return [];

    const filtered = localAppointments.filter(appointment => {
      // Exclude any items that are meetings from the main appointments list
      if (appointment?.type === 'meeting') {
        return false;
      }
      if (!appointment) {
        return false;
      }

      try {
        const appointmentDate = convertToDate(appointment.dateTime);
        
        if (!appointmentDate) {
          return false;
        }

        // Compare dates only, not times
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const appointmentDay = new Date(appointmentDate);
        appointmentDay.setHours(0, 0, 0, 0);
        
        // Check if it's today or in the future
        const isNotPast = appointmentDay >= today;
        if (!isNotPast) {
          return false;
        }

        // Check if it's not archived and not in archives collection
        const isNotArchived = !appointment.archived && !appointment.archivedAt;
        if (!isNotArchived) {
          return false;
        }

        // Check if it's not cancelled
        const isNotCancelled = appointment.status !== 'cancelled';
        if (!isNotCancelled) {
          return false;
        }

        return true;
      } catch (error) {
        return false;
      }
    });

    // Sort by dateTime, with completed appointments at the bottom
    return sortAppointmentsByDateTimeCompletedLast(filtered, convertToDate);
  }, [localAppointments]);

  const getPendingAppointments = useCallback(() => {
    return localAppointments.filter(apt => {
      if (!apt) return false;

      try {
        const appointmentDate = convertToDate(apt.dateTime);
        const status = String(apt.status || '').toLowerCase();
        const awaitingClient =
          apt.clientConfirmed !== true &&
          (status === 'confirmed' || status === 'pending') &&
          status !== 'pending_payment' &&
          status !== 'processing';

        return awaitingClient &&
               !isNaN(appointmentDate.getTime()) &&
               appointmentDate > new Date() &&
               !apt.archived;
      } catch (error) {
        return false;
      }
    });
  }, [localAppointments]);

  // Add this function after getPendingAppointments
  const getTodayAppointments = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return localAppointments.filter(apt => {
      const appointmentDate = convertToDate(apt?.dateTime);
      return appointmentDate && 
             appointmentDate >= today &&
             appointmentDate < tomorrow &&
             !apt.archived;
    });
  }, [localAppointments]);

  return (
    <>
      <div className="overview-cards-container">
        {/* Stats Cards */}
        <div className="overview-card quick-actions-card">
          <QuickActionsColumn setIsAddModalOpen={setIsAddModalOpen} />
        </div>
        <div className="overview-card today-appointments-card overview-card--mobile-nav-target">
          <TodayAppointmentsCard
            todayAppointments={getTodayAppointments()}
            loading={appointmentsLoading}
            onViewAll={() => {
              setViewAllType('today');
              setIsViewAllOpen(true);
            }}
            onAppointmentClick={handleAppointmentClick}
          />
        </div>
        <div className="overview-card pending-confirmations-card overview-card--mobile-nav-target">
          <PendingConfirmationsCard
            pendingAppointments={getPendingAppointments()}
            loading={appointmentsLoading}
            onViewAll={() => {
              setViewAllType('pending');
              setIsViewAllOpen(true);
            }}
            onAppointmentClick={handleAppointmentClick}
          />
        </div>
        <div className="overview-card recent-updates-card overview-card--mobile-nav-target">
          <RecentUpdatesCard
            updates={updates}
            loading={updatesLoading}
            companyId={companyId}
            onUpdateRead={onUpdateRead}
            onClearAllRead={onClearAllRead}
          />
        </div>

        {/* Upcoming Appointments - Full Width */}
        <div className="overview-card overview-card-full upcoming-appointments-full-width">
          <UpcomingAppointmentsCard
            appointments={filteredAppointments}
            loading={appointmentsLoading}
            onViewAll={() => setIsViewAllDialogOpen(true)}
            onAppointmentClick={handleAppointmentClick}
            handleQuickConfirm={handleQuickConfirm}
            handleQuickCancel={handleQuickCancel}
            handleQuickReschedule={handleQuickReschedule}
            handleQuickNoShow={handleQuickNoShow}
            setConfirmationDialog={setConfirmationDialog}
            getServiceName={getServiceName}
          />
        </div>
      </div>
    </>
  );
};

const Overview = ({ setIsAuthenticated }) => {
  // Auth and permission hooks
  const [loading, setLoading] = useState(true);
  const { user, companyData, loading: authLoading } = useAuth();
  const { hasPermission } = usePermissions();
  const { showToast } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const showEmailVerificationBanner = useAppEmailVerificationRestricted();

  // All state hooks
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [selectedPage, setSelectedPage] = useState('dashboard');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    client: '',
    service: '',
    serviceTags: [],
    status: 'pending',
    notes: '',
    date: new Date(),
    time: new Date(),
    duration: 60
  });
  const [availableTags, setAvailableTags] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  useAppointmentAutoArchiver(companyData?.id);
  useMeetingAutoArchiver(companyData?.id);
  useBackgroundAppointmentCompletion(companyData?.id);
  const {
    updates,
    loading: updatesLoading,
    addUpdate,
    addRawUpdate,
    markUpdatesReadLocal,
  } = useAppointmentUpdates(companyData?.id);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    type: null,
    appointment: null
  });
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [localAppointments, setLocalAppointments] = useState([]);
  const [totalClientCount, setTotalClientCount] = useState(0);
  const [isViewAllDialogOpen, setIsViewAllDialogOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [appVersionState, setAppVersionState] = useState({
    version: localStorage.getItem('lastVersionCheck') || '',
    cacheKey: localStorage.getItem('lastCacheKey') || '',
    invalidationKey: localStorage.getItem('lastInvalidationKey') || ''
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);
  const [meetingCount, setMeetingCount] = useState(0);
  const [meetingNavCounts, setMeetingNavCounts] = useState({ todayCount: 0, pendingCount: 0 });

  const appointmentMobileNavCounts = useAppointmentMobileNavCounts(appointments);
  const unreadUpdatesCount = useMemo(
    () => updates.filter((update) => !update.read).length,
    [updates],
  );
  const mobileNavMode = getOverviewMobileNavMode(location.pathname);
  const mobileNavCounts = mobileNavMode === 'meetings'
    ? meetingNavCounts
    : appointmentMobileNavCounts;

  // Memoized values
  const today = useMemo(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }, []);

  // Callbacks
  const loadOverviewAppointments = useCallback(async () => {
    if (!user?.uid || !companyData?.id) return;

    try {
      const rangeStart = startOfDay(subDays(new Date(), OVERVIEW_APPOINTMENT_PAST_DAYS));
      const rangeEnd = endOfDay(addDays(new Date(), OVERVIEW_APPOINTMENT_FUTURE_DAYS));
      const result = await listAppointments({
        companyId: companyData.id,
        from: rangeStart,
        to: rangeEnd,
        archived: false,
      });
      const rows = (result.appointments || [])
        .map((data) =>
          normalizeOverviewAppointment({
            id: data.id,
            data,
            storageLocation: COMPANY_APPOINTMENT_STORAGE,
          })
        )
        .filter((appointment) => appointment.dateTime && !appointment.archived);
      rows.sort((a, b) => a.dateTime - b.dateTime);
      setAppointments(rows);
    } catch {
      /* keep last successful list */
    } finally {
      setAppointmentsLoading(false);
    }
  }, [user?.uid, companyData?.id]);

  const handleAppointmentUpdate = useCallback(() => {
    void loadOverviewAppointments();
  }, [loadOverviewAppointments]);

  // Add a debug logger that only logs in development
  const debugLog = () => {};

  // Add an error logger that only logs non-permission errors
  const errorLog = () => {};

  const fetchAppointmentsData = useCallback(() => {
    if (!user?.uid || !companyData?.id) return () => {};

    setAppointmentsLoading(true);
    void loadOverviewAppointments();
    const poll = setInterval(() => {
      void loadOverviewAppointments();
    }, 12000);
    return () => clearInterval(poll);
  }, [user?.uid, companyData?.id, loadOverviewAppointments]);

  // Listen for service worker updates and refresh data when needed
  useEffect(() => {
    // Listen for service worker controller changes (indicates an update)
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Force refresh appointments data
        if (user && companyData) {
          void loadOverviewAppointments();
        }
      });
    }

    // Check for version changes in localStorage
    const checkVersionChanges = () => {
      const currentVersion = localStorage.getItem('lastVersionCheck');
      const currentCacheKey = localStorage.getItem('lastCacheKey');
      const currentInvalidationKey = localStorage.getItem('lastInvalidationKey');

      if (
        currentVersion !== appVersionState.version ||
        currentCacheKey !== appVersionState.cacheKey ||
        currentInvalidationKey !== appVersionState.invalidationKey
      ) {
        setAppVersionState({
          version: currentVersion || '',
          cacheKey: currentCacheKey || '',
          invalidationKey: currentInvalidationKey || ''
        });
        
        // Force refresh appointments data
        if (user && companyData) {
          void loadOverviewAppointments();
        }
      }
    };

    // Check version changes every 30 seconds
    const versionCheckInterval = setInterval(checkVersionChanges, 30000);

    return () => {
      clearInterval(versionCheckInterval);
    };
  }, [user, companyData, appVersionState, loadOverviewAppointments]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Updates every second

    return () => clearInterval(timer);
  }, []);

  const userProfile = user;

  const handleOpenAddModal = (date = null) => {
    setSelectedDate(date);
    setIsAddModalOpen(true);
  };
  
  useEffect(() => {
    const fetchData = async () => {
      if (!user || !companyData) return;
      try {
        const fetchedClients = (await listClients({
          companyId: companyData.id,
          archived: false,
        }))
          .filter((data) => data.id !== 'default' && !data.isSystem && !data.isDeleted && !data.archived)
          .map((data) => ({
            id: data.id,
            name: data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim() || `Client ${data.id}`,
            ...data,
          }));

        setClients(fetchedClients);
  
        // Skip fetching service tags since they're not used in the Overview component
      } catch (error) {
        // Silently handle permission errors
        if (error.code === 'permission-denied') {
          return;
        }
      }
    };
  
    fetchData();
  }, [user?.uid, companyData?.id]);

  const totalClients = new Set(clients.map(client => client.name)).size;

  const handleNewNotification = (message, severity = 'success') => {
    showToast(message, severity);
  };

  const getUpdaterMetadata = () => {
    const firstName = userProfile?.firstName?.trim?.() || '';
    const lastName = userProfile?.lastName?.trim?.() || '';
    const combinedName = `${firstName} ${lastName}`.trim();
    const fallbackName =
      userProfile?.displayName ||
      userProfile?.name ||
      user?.displayName ||
      user?.email?.split('@')?.[0] ||
      '';

    return {
      updatedByName: combinedName || fallbackName || undefined,
      updatedByUid: user?.uid || undefined
    };
  };

  const generateRecurringAppointments = (appointmentData) => {
    const { date, time, recurringType, recurringEndDate, ...rest } = appointmentData;
    const startDate = new Date(`${date}T${time}`);
    const endDate = new Date(recurringEndDate);
    const appointments = [];
  
    let currentDate = startDate;
    while (isBefore(currentDate, endDate) || isEqual(currentDate, endDate)) {
      appointments.push({
        ...rest,
        date: format(currentDate, 'yyyy-MM-dd'),
        time: format(currentDate, 'HH:mm'),
      });
  
      switch (recurringType) {
        case 'daily':
          currentDate = addDays(currentDate, 1);
          break;
        case 'weekly':
          currentDate = addWeeks(currentDate, 1);
          break;
        case 'monthly':
          currentDate = addMonths(currentDate, 1);
          break;
        default:
          break;
      }
    }
  
    return appointments;
  };

  const handleAddAppointment = async (appointmentData) => {
    try {
      if (!user || !companyData) {
        throw new Error('Missing required data');
      }

      const appointmentId = crypto.randomUUID();
      const dataWithSlug = {
        ...appointmentData,
        id: appointmentId,
        companyId: companyData.id,
        shortSlug: generateShortSlug(),
      };
      await upsertAppointment({
        companyId: companyData.id,
        id: appointmentId,
        appointment: dataWithSlug,
      });

      void addRawUpdate({
        message: `New appointment scheduled for ${appointmentData.clientName || 'Client'} on ${
          format(new Date(appointmentData.dateTime), 'PPp')
        }`,
        type: 'created',
      }).catch((error) => {
        console.warn('Non-blocking: failed to write updates record', error);
      });

      handleNewNotification("Appointment added successfully", "success");
      void loadOverviewAppointments();
      return appointmentId;
    } catch (error) {
      handleNewNotification("Failed to add appointment. Please try again.", "error");
      throw error;
    }
  };

  const companyDisplayName = companyData?.name || 'Company Name';

  const handleQuickConfirm = async (appointment) => {
    try {
      const userRole = userProfile?.role?.toLowerCase() || 'employee';
      const updatedBy = userRole === 'client' ? 'client' : 'employee';
      const { updatedByName, updatedByUid } = getUpdaterMetadata();
      const now = new Date().toISOString();

      await upsertAppointment({
        companyId: companyData.id,
        id: appointment.id,
        appointment: {
          status: 'confirmed',
          updatedAt: now,
          updatedBy,
          updatedByName: updatedByName || null,
          updatedByUid: updatedByUid || null,
          lastAction: 'confirmed',
          clientConfirmed: appointment.clientConfirmed || false,
        },
      });

      void addUpdate(appointment, 'confirmed', {
        updatedBy,
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'confirmed',
        clientConfirmed: appointment.clientConfirmed || false,
      }).catch((error) => {
        console.warn('Non-blocking: failed to write updates record', error);
      });

      showToast("Appointment confirmed successfully", "success");
      handleAppointmentUpdate();
    } catch (error) {
      showToast("Failed to confirm appointment", "error");
    }
  };

  const handleQuickCancel = async (appointment) => {
    try {
      const cancelReason = appointment?.cancelReason?.trim();
      if (!cancelReason) {
        throw new Error('Cancellation reason is required');
      }
      if (!companyData?.id) {
        throw new Error('Company not loaded');
      }

      const userRole = userProfile?.role?.toLowerCase() || 'employee';
      const updatedBy = userRole === 'client' ? 'client' : 'employee';
      const { updatedByName, updatedByUid } = getUpdaterMetadata();

      await archiveCompanyAppointment(null, companyData.id, appointment, {
        status: 'cancelled',
        cancelReason,
        updatedBy,
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'cancelled',
        archivedBy: companyData.id,
      });

      void addUpdate(appointment, 'cancelled', {
        updatedBy,
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'cancelled',
        cancelReason,
        service: getServiceName(appointment),
      }).catch((error) => {
        console.warn('Non-blocking: failed to write updates record', error);
      });

      showToast('Appointment cancelled successfully', 'success');
      handleAppointmentUpdate();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      const message = error?.code === 'permission-denied'
        ? 'You do not have permission to cancel this appointment.'
        : (error?.message || 'Failed to cancel appointment');
      showToast(message, 'error');
    }
  };

  const handleQuickReschedule = async (appointmentId, newDateTime) => {
    try {
      if (!appointmentId) {
        throw new Error('No appointment ID provided');
      }

      // Ensure newDateTime is a valid Date object
      const dateToUse = newDateTime instanceof Date ? newDateTime : new Date(newDateTime);
      
      if (isNaN(dateToUse.getTime())) {
        throw new Error('Invalid date/time provided');
      }

      // Find the appointment in the current appointments list
      const appointment = appointments.find(apt => apt.id === appointmentId);
      if (!appointment) {
        throw new Error('Appointment not found');
      }

      // Get the user's role
      const userRole = userProfile?.role?.toLowerCase() || 'employee';
      const updatedBy = userRole === 'client' ? 'client' : 'employee';
      const { updatedByName, updatedByUid } = getUpdaterMetadata();

      const originalDateTime = appointment.dateTime instanceof Date
        ? appointment.dateTime
        : appointment.dateTime?.toDate
          ? appointment.dateTime.toDate()
          : new Date(appointment.dateTime);
      const now = new Date().toISOString();
      const durationMinutes = Number(appointment.duration) > 0 ? Number(appointment.duration) : 60;
      const endTime = new Date(dateToUse.getTime() + durationMinutes * 60_000);

      await upsertAppointment({
        companyId: companyData.id,
        id: appointmentId,
        appointment: {
          dateTime: dateToUse.toISOString(),
          endTime: endTime.toISOString(),
          previousDateTime: originalDateTime.toISOString(),
          status: 'rescheduled',
          updatedAt: now,
          updatedBy,
          updatedByName: updatedByName || null,
          updatedByUid: updatedByUid || null,
          lastAction: 'rescheduled',
          reminderSent: false,
          reminderSMSSent: false,
          automatedCheckInReminderSent: false,
        },
      });

      void addUpdate(appointment, 'rescheduled', {
        updatedBy,
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'rescheduled',
        previousDateTime: originalDateTime,
        dateTime: dateToUse,
        service: getServiceName(appointment),
      }).catch((updateError) => {
        console.warn('Non-blocking: failed to write updates record', updateError);
      });

      handleNewNotification('Appointment rescheduled successfully', 'success');
      handleAppointmentUpdate();
    } catch (error) {
      handleNewNotification('Failed to reschedule appointment: ' + error.message, 'error');
    }
  };

  const handleConfirmClick = (appointment) => {
    setConfirmationDialog({
      open: true,
      type: 'confirm',
      appointment
    });
  };

  const handleCancelClick = (appointment) => {
    setConfirmationDialog({
      open: true,
      type: 'cancel',
      appointment
    });
  };

  const handleRescheduleClick = (appointment) => {
    setConfirmationDialog({
      open: true,
      type: 'reschedule',
      appointment
    });
  };

  // Add getServiceName function
  const getServiceName = (appointment) => {
    if (appointment.serviceTags && appointment.serviceTags.length > 0) {
      const tag = appointment.serviceTags[0];
      return typeof tag === 'object' ? tag.name : tag;
    }
    if (appointment.service) {
      if (typeof appointment.service === 'object' && appointment.service.name) {
        return appointment.service.name;
      }
      if (typeof appointment.service === 'string') {
        return appointment.service;
      }
    }
    return 'No Service';
  };

  const handleUpdateRead = (updateId) => {
    markUpdatesReadLocal(updateId);
  };

  const handleClearAllUpdatesRead = (updateIds) => {
    markUpdatesReadLocal(updateIds);
  };

  const headerMetrics = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    
    return {
      todayAppointments: appointments.filter(apt => {
        try {
          const appointmentDate = apt.dateTime instanceof Date 
            ? apt.dateTime 
            : apt.dateTime.toDate?.() || new Date(apt.dateTime);
          
          return format(appointmentDate, 'yyyy-MM-dd') === today;
        } catch (error) {
          return false;
        }
      }).length,
      
      pendingAppointments: appointments.filter(apt => {
        const status = String(apt.status || '').toLowerCase();
        return apt.clientConfirmed !== true &&
          (status === 'confirmed' || status === 'pending') &&
          status !== 'pending_payment';
      }).length,
      
      totalClients: totalClients || 0
    };
  }, [appointments, totalClients]);

  const handleSignOut = useCallback(() => {
    setAnchorEl(null);
    setSignOutDialogOpen(true);
  }, []);

  const handleConfirmSignOut = useCallback(async () => {
    setSignOutDialogOpen(false);
    try {
      await auth.signOut();
    } catch (error) {
      // Auth / router typically handles redirect; stay quiet here
    }
  }, []);

  const handleMenuClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleQuickNoShow = async (appointment) => {
    try {
      const userRole = userProfile?.role?.toLowerCase() || 'employee';
      const updatedBy = userRole === 'client' ? 'client' : 'employee';
      const { updatedByName, updatedByUid } = getUpdaterMetadata();

      await archiveCompanyAppointment(null, companyData.id, appointment, {
        status: 'no show',
        updatedBy,
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'no show',
      });

      void addUpdate(appointment, 'no show', {
        updatedBy,
        updatedByName: updatedByName || null,
        updatedByUid: updatedByUid || null,
        lastAction: 'no show',
        service: getServiceName(appointment),
      }).catch((error) => {
        console.warn('Non-blocking: failed to write updates record', error);
      });

      showToast("Appointment marked as no show", "success");

      // Force refresh appointments
      handleAppointmentUpdate();
    } catch (error) {
      showToast("Failed to mark appointment as no show", "error");
    }
  };

  useEffect(() => {
    if (!user?.uid || !companyData?.id) return undefined;

    let cancelled = false;
    const loadClientCount = async () => {
      try {
        const activeClients = (await listClients({
          companyId: companyData.id,
          archived: false,
        })).filter((data) => data.id !== 'default' && !data.isSystem && !data.isDeleted && !data.archived);
        if (!cancelled) setTotalClientCount(activeClients.length);
      } catch {
        if (!cancelled) setTotalClientCount(0);
      }
    };
    loadClientCount();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, companyData?.id]);

  // Add scroll listener effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initial fetch of appointments
  useEffect(() => {
    if (!user?.uid || !companyData?.id) return undefined;
    const unsubscribe = fetchAppointmentsData();
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [user?.uid, companyData?.id, fetchAppointmentsData]);

  // Update upcomingAppointments whenever appointments changes
  useEffect(() => {
    setUpcomingAppointments(appointments);
    setLocalAppointments(appointments);
  }, [appointments]);

  // Subscribe to meetings count for tab badge (match MeetingsOverview: today or future days only;
  // exclude cancelled, archived, and past-calendar-day rows until meeting auto-archiver runs)
  useEffect(() => {
    if (!companyData?.id) return undefined;

    let cancelled = false;
    const loadMeetingCounts = async () => {
      try {
        const rows = await listMeetings({ companyId: companyData.id, archived: false });
        if (cancelled) return;
        const startToday = new Date();
        startToday.setHours(0, 0, 0, 0);
        const tomorrow = new Date(startToday);
        tomorrow.setDate(tomorrow.getDate() + 1);

        let upcomingCount = 0;
        let todayCount = 0;
        let pendingCount = 0;

        rows.forEach((data) => {
          const status = (data.status || '').toLowerCase();
          if (status === 'cancelled' || status === 'canceled') return;
          if (data.archived || data.archivedAt) return;
          const dateTime = convertToDate(data.dateTime);
          if (!dateTime) return;
          const day = new Date(dateTime);
          day.setHours(0, 0, 0, 0);
          if (day < startToday) return;

          upcomingCount += 1;
          if (dateTime >= startToday && dateTime < tomorrow) {
            todayCount += 1;
          }
          if (status === 'pending') {
            pendingCount += 1;
          }
        });

        setMeetingCount(upcomingCount);
        setMeetingNavCounts({ todayCount, pendingCount });
      } catch (error) {
        console.error('Error loading meeting counts:', error);
      }
    };

    loadMeetingCounts();
    const poll = setInterval(loadMeetingCounts, 12000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [companyData?.id]);

  // Permission check effect
  useEffect(() => {
    if (!authLoading) {
      if (!hasPermission('overview.view')) {
        navigate('/unauthorized', { replace: true });
      } else {
        setLoading(false);
      }
    }
  }, [authLoading, hasPermission, navigate]);



  // Loading check
  if (loading || authLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (showEmailVerificationBanner && !isAllowedPathWhileEmailUnverified(location.pathname)) {
    return <Navigate to="/app/overview" replace />;
  }

  return (
    <div className={`overview-container${isOverviewMobileNavPath(location.pathname) ? ' overview-container--mobile-bottom-nav' : ''}${showEmailVerificationBanner ? ' overview-container--email-unverified' : ''}`}>
      {showEmailVerificationBanner && <AppEmailVerificationBanner />}
      <header className={`overview-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="overview-header-toolbar">
          {/* Left section - Menu button */}
          <div className="overview-header-left">
            <button
              className="overview-header-menu-button"
              aria-label="navigation menu"
              onClick={handleMenuClick}
            >
              <Menu open={isMenuOpen} className="menu-icon" />
            </button>
            <span className="menu-text">Menu</span>
          </div>

          {/* Brand + Appointments / Meetings tabs — grid-placed (true center on desktop) */}
          <div
            className="overview-header-brand"
            role="link"
            tabIndex={0}
            onClick={() => navigate('/app/overview')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/app/overview');
              }
            }}
          >
            <h1 className="overview-header-company-name">
              {companyDisplayName}
            </h1>
            {companyData?.logoUrl && (
              <img
                src={companyData.logoUrl}
                alt=""
                className="overview-header-logo"
              />
            )}
          </div>
          <div className="overview-header-tabs overview-header-tabs--desktop">
            <OverviewTabs
              appointmentCount={upcomingAppointments.length}
              meetingCount={meetingCount}
            />
          </div>

          {/* Right section - Theme toggle */}
          <div className="overview-header-right">
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Navigation Menu */}
      {isMenuOpen && (
        <div className="navigation-menu-container" onClick={handleMenuClose}>
          <div className="navigation-menu" onClick={(e) => e.stopPropagation()}>
            <NavigationMenu 
              handleMenuClose={handleMenuClose}
              handleSignOut={handleSignOut}
            />
          </div>
        </div>
      )}

      <main className={`overview-main${isOverviewMobileNavPath(location.pathname) ? ' overview-main--mobile-bottom-nav' : ''}`}>
        <MaintenanceNoticeBanner />
        {/* Tutorial Banner - at top of main content */}
        <TutorialChecklist />

        <div className="overview-mobile-tabs-strip">
          <OverviewTabs
            appointmentCount={upcomingAppointments.length}
            meetingCount={meetingCount}
          />
        </div>
        
        <OverviewUserProvider value={userProfile}>
        {location.pathname === '/app/overview' ? (
          <UpcomingAppointments 
            appointments={upcomingAppointments} 
            totalClients={totalClients} 
            setIsAddModalOpen={setIsAddModalOpen}
            setSelectedPage={setSelectedPage} 
            handleQuickConfirm={handleQuickConfirm}
            handleQuickCancel={handleQuickCancel}
            handleQuickReschedule={handleQuickReschedule}
            handleQuickNoShow={handleQuickNoShow}
            handleConfirmClick={handleConfirmClick}
            handleCancelClick={handleCancelClick}
            handleRescheduleClick={handleRescheduleClick}
            setConfirmationDialog={setConfirmationDialog}
            getServiceName={getServiceName}
            updates={updates}
            handleAppointmentUpdate={handleAppointmentUpdate}
            companyId={companyData?.id}
            onUpdateRead={handleUpdateRead}
            onClearAllRead={handleClearAllUpdatesRead}
            appointmentsLoading={appointmentsLoading}
            updatesLoading={updatesLoading}
          />
        ) : location.pathname === '/app/overview/today' ? (
          <OverviewMobileTodayPanel
            appointments={appointments}
            loading={appointmentsLoading}
            onAppointmentClick={(appointment) => {
              if (!appointment?.id) return;
              const appointmentSegment = getCompanyAppointmentUrlSegment(appointment);
              navigate(`/app/overview/appointment/${appointmentSegment}`, {
                state: { appointment },
              });
            }}
          />
        ) : location.pathname === '/app/overview/pending' ? (
          <OverviewMobilePendingPanel
            appointments={appointments}
            loading={appointmentsLoading}
            onAppointmentClick={(appointment) => {
              if (!appointment?.id) return;
              const appointmentSegment = getCompanyAppointmentUrlSegment(appointment);
              navigate(`/app/overview/appointment/${appointmentSegment}`, {
                state: { appointment },
              });
            }}
          />
        ) : location.pathname === '/app/overview/updates' ? (
          <OverviewMobileUpdatesPanel
            updates={updates}
            loading={updatesLoading}
            companyId={companyData?.id}
            onUpdateRead={handleUpdateRead}
            onClearAllRead={handleClearAllUpdatesRead}
          />
        ) : location.pathname === '/app/overview/meetings/today' ? (
          <MeetingsMobileTodayPanel />
        ) : location.pathname === '/app/overview/meetings/pending' ? (
          <MeetingsMobilePendingPanel />
        ) : location.pathname === '/app/overview/meetings' ? (
          <Outlet />
        ) : location.pathname.startsWith('/app/overview/meetings/') ? (
          <Outlet />
        ) : location.pathname === '/app/overview/appointments' ? (
          <AppointmentsPage 
            appointments={appointments}
            onAppointmentClick={(appointment) => {
              if (!appointment?.id) return;
              const appointmentSegment = getCompanyAppointmentUrlSegment(appointment);
              navigate(`/app/overview/appointment/${appointmentSegment}`, {
                state: { appointment }
              });
            }}
            handleQuickConfirm={handleQuickConfirm}
            handleQuickCancel={handleQuickCancel}
            handleQuickReschedule={handleQuickReschedule}
            handleQuickNoShow={handleQuickNoShow}
            setConfirmationDialog={setConfirmationDialog}
            getServiceName={getServiceName}
          />
        ) : (
          <EmailVerificationOverviewGuard>
            <Outlet />
          </EmailVerificationOverviewGuard>
        )}
        </OverviewUserProvider>
      </main>

      <OverviewSideNav
        meetingCount={meetingCount}
        onSignOut={handleSignOut}
      />

      <OverviewMobileBottomNav
        todayCount={mobileNavCounts.todayCount}
        pendingCount={mobileNavCounts.pendingCount}
        updatesCount={unreadUpdatesCount}
      />

      <SignOutConfirmDialog
        open={signOutDialogOpen}
        onCancel={() => setSignOutDialogOpen(false)}
        onConfirm={handleConfirmSignOut}
      />
    </div>
  );
};

export default Overview;